import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Briefcase, Wallet, CreditCard, FileText, FileSpreadsheet, FileSignature, Home, BadgeCheck, Building2, LineChart } from "lucide-react";

const AnalyzerFlow = ({ loanId }) => {
  const [analyzers, setAnalyzers] = useState([]);
  const [pendingAnalyzers, setPendingAnalyzers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  console.log("AnalyzerFlow rendered with loanId:", loanId);

  useEffect(() => {
    if (loanId) {
      fetchAnalyzers();
    }
  }, [loanId]);

  const fetchAnalyzers = async () => {
    try {
      console.log("Fetching analyzers for loanId:", loanId);
      const response = await fetch(`https://dashboard.lono.co.in/api/analyzer-workflow/getanalysers/${loanId}`);
      const data = await response.json();
      
      console.log("Analyzer API response:", data);
      
      if (data.success) {
        setAnalyzers(data.analyzers || []);
        setPendingAnalyzers(data.pendingAnalyzers || []);
        setMessage(data.message || "");
      }
    } catch (error) {
      console.error("Error fetching analyzers:", error);
    } finally {
      setLoading(false);
    }
  };

  const getFormattedDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "dd MMM, yyyy - h:mm a");
    } catch (error) {
      console.error("Error formatting date time:", error);
      return "";
    }
  };

  const getDotColor = (status) => {
    return status === "completed" ? "bg-green-500" : "bg-red-500";
  };

  const getAnalyzerIcon = (type) => {
    const iconProps = { size: 30, className: "text-white" };
    switch (type) {
      case "business-profile":
        return <Briefcase {...iconProps} />;
      case "universal-bank-statement":
        return <Wallet {...iconProps} />;
      case "universal-credit-report":
        return <CreditCard {...iconProps} />;
      case "payslip":
        return <FileText {...iconProps} />;
      case "itr":
        return <FileSpreadsheet {...iconProps} />;
      case "loan-statement":
        return <FileSignature {...iconProps} />;
      case "rental-agreement":
        return <Home {...iconProps} />;
      case "business-license":
        return <BadgeCheck {...iconProps} />;
      case "property-document":
        return <Building2 {...iconProps} />;
      case "evaluation-board":
        return <LineChart {...iconProps} />;
      default:
        return <FileText {...iconProps} />;
    }
  };

  const getAnalyzerName = (type) => {
    switch (type) {
      case "universal-credit-report":
        return "Universal Credit Report";
      case "universal-bank-statement":
        return "Universal Bank Statement";
      case "business-profile":
        return "Business Profile";
      case "payslip":
        return "Payslip";
      case "itr":
        return "ITR";
      case "loan-statement":
        return "Loan Statement";
      case "rental-agreement":
        return "Rental Agreement";
      case "business-license":
        return "Business License";
      case "property-document":
        return "Property Document";
      case "evaluation-board":
        return "Evaluation Board";
      default:
        return type.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  if (loading) {
    return (
      <div className="bg-[#131313] rounded-lg p-4 mb-4">
        <div className="text-white">Loading analyzer workflow...</div>
      </div>
    );
  }

  return (
    <div className="bg-[#131313] rounded-lg p-4 mb-4">
      {/* Header */}
      <div className="flex flex-wrap justify-between mb-4 gap-2">
        <button className="px-4 py-2 rounded-full flex items-center bg-[#FFCA20] text-black">
          <span className="mr-1">🔍</span> Analyzer Workflow
        </button>
      </div>

      {/* Timeline Section */}
      <div className="pl-16 relative">
        {/* Timeline vertical line */}
        <div className="absolute left-6 top-2 bottom-0 w-0.5 bg-[#FFCA20]"></div>

        {/* Completed Analyzers */}
        {analyzers.map((analyzer, index) => (
          <div key={`completed-${index}`} className="mb-8 relative">
            <div
              className={`absolute -left-14 mt-2 w-10 h-10 rounded-full ${getDotColor(
                "completed"
              )} flex items-center justify-center`}
            >
              {getAnalyzerIcon(analyzer.type)}
            </div>
            <p className="text-white text-lg font-medium">
              {getAnalyzerName(analyzer.type)} Completed
            </p>
            <p className="text-gray-400 text-sm">
              {getFormattedDateTime(analyzer.completedDate)}
            </p>
          </div>
        ))}

        {/* Pending Analyzers */}
        {pendingAnalyzers.map((analyzer, index) => (
          <div key={`pending-${index}`} className="mb-8 relative">
            <div
              className={`absolute -left-14 mt-2 w-10 h-10 rounded-full ${getDotColor(
                "pending"
              )} flex items-center justify-center`}
            >
              {getAnalyzerIcon(analyzer.type)}
            </div>
            <p className="text-white text-lg font-medium">
              {getAnalyzerName(analyzer.type)} Pending
            </p>
            <p className="text-gray-400 text-sm">
              Waiting to be processed
            </p>
          </div>
        ))}


      </div>
    </div>
  );
};

export default AnalyzerFlow;